import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  SimSubmissionInterface,
  ArtSubmissionInterface
} from '../models/FreezePeriodInterface';

dayjs.extend(utc);

export interface SimAccessInfo {
  isAccessible: boolean;
  currentPeriod?: SimSubmissionInterface;
  nextPeriod?: SimSubmissionInterface;
  reason?: string;
}

export interface ArtAccessInfo {
  isAccessible: boolean;
  currentPeriod?: ArtSubmissionInterface;
  nextPeriod?: ArtSubmissionInterface;
  reason?: string;
}

/**
 * Check if pilots should have access to the SIM/ART request form
 * Access is granted when:
 * 1. There's an active submission period (has href)
 * 2. Current date falls within the from_date and to_date range
 *
 * @param simSubmission - Array of SIM submission periods from freeze period context
 * @returns Access information object
 */
export function checkSimAccess(
  simSubmission?: SimSubmissionInterface[]
): SimAccessInfo {
  if (!simSubmission || simSubmission.length === 0) {
    return {
      isAccessible: false,
      reason: 'No SIM submission periods available'
    };
  }

  const now = dayjs().utc().utcOffset(-5); // Eastern Time (EST/EDT)

  // Find current active period (has href and current date is within range)
  const currentPeriod = simSubmission.find((period) => {
    if (!period.href || !period.from_date || !period.to_date) {
      return false;
    }

    const fromDate = dayjs(period.from_date).utc().utcOffset(-5);
    const toDate = dayjs(period.to_date).utc().utcOffset(-5).endOf('day'); // Include the entire end date

    return now.isBetween(fromDate, toDate, null, '[]'); // [] means inclusive on both ends
  });

  if (currentPeriod) {
    return {
      isAccessible: true,
      currentPeriod,
      reason: 'Access granted - within submission period'
    };
  }

  // Find next available period
  const futurePeriods = simSubmission
    .filter(
      (period) => period.from_date && dayjs(period.from_date).isAfter(now)
    )
    .sort((a, b) => dayjs(a.from_date).diff(dayjs(b.from_date)));

  const nextPeriod = futurePeriods[0];

  return {
    isAccessible: false,
    nextPeriod,
    reason: nextPeriod
      ? 'Access restricted - outside submission period'
      : 'No future submission periods available'
  };
}

/**
 * Format the submission period dates for display with time information
 */
export function formatSubmissionPeriod(period: SimSubmissionInterface): string {
  if (!period.from_date || !period.to_date) {
    return 'Invalid period';
  }

  const fromDate = dayjs(period.from_date).utc().utcOffset(-5);
  const toDate = dayjs(period.to_date).utc().utcOffset(-5);

  // Format time as HH:MM ET
  const fromTime = fromDate.format('HH:mm');
  const toTime = toDate.format('HH:mm');

  // If same month
  if (fromDate.month() === toDate.month()) {
    return `${fromDate.format('MMMM D')} @ ${fromTime} ET - ${toDate.format('D, YYYY')} @ ${toTime} ET`;
  }

  // Different months
  return `${fromDate.format('MMMM D')} @ ${fromTime} ET - ${toDate.format('MMMM D, YYYY')} @ ${toTime} ET`;
}

/**
 * Get the month name for the SIM period
 */
export function getSimPeriodMonth(period: SimSubmissionInterface): string {
  if (!period.sim_month) {
    return 'Unknown';
  }

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  const monthIndex = parseInt(period.sim_month) - 1;
  return monthNames[monthIndex] || 'Unknown';
}

/**
 * Check if pilots should have access to the ART request form
 * Access is granted when:
 * 1. There's an active submission period (has href)
 * 2. Current date falls within the from_date and to_date range
 *
 * @param artSubmission - Array of ART submission periods from freeze period context
 * @returns Access information object
 */
export function checkArtAccess(
  artSubmission?: ArtSubmissionInterface[]
): ArtAccessInfo {
  if (!artSubmission || artSubmission.length === 0) {
    return {
      isAccessible: false,
      reason: 'No ART submission periods available'
    };
  }

  const now = dayjs().utc().utcOffset(-5); // Eastern Time (EST/EDT)

  // Find current active period (has href and current date is within range)
  const currentPeriod = artSubmission.find((period) => {
    if (!period.href || !period.from_date || !period.to_date) {
      return false;
    }

    const fromDate = dayjs(period.from_date).utc().utcOffset(-5);
    const toDate = dayjs(period.to_date).utc().utcOffset(-5).endOf('day'); // Include the entire end date

    return now.isBetween(fromDate, toDate, null, '[]'); // [] means inclusive on both ends
  });

  if (currentPeriod) {
    return {
      isAccessible: true,
      currentPeriod,
      reason: 'Access granted - within ART submission period'
    };
  }

  // Find next available period
  const futurePeriods = artSubmission
    .filter(
      (period) => period.from_date && dayjs(period.from_date).isAfter(now)
    )
    .sort((a, b) => dayjs(a.from_date).diff(dayjs(b.from_date)));

  const nextPeriod = futurePeriods[0];

  return {
    isAccessible: false,
    nextPeriod,
    reason: nextPeriod
      ? 'Access restricted - outside ART submission period'
      : 'No future ART submission periods available'
  };
}

/**
 * Format the ART submission period dates for display with time information
 */
export function formatArtSubmissionPeriod(
  period: ArtSubmissionInterface
): string {
  if (!period.from_date || !period.to_date) {
    return 'Invalid period';
  }

  const fromDate = dayjs(period.from_date).utc().utcOffset(-5);
  const toDate = dayjs(period.to_date).utc().utcOffset(-5);

  // Format time as HH:MM ET
  const fromTime = fromDate.format('HH:mm');
  const toTime = toDate.format('HH:mm');

  // If same month
  if (fromDate.month() === toDate.month()) {
    return `${fromDate.format('MMMM D')} @ ${fromTime} ET - ${toDate.format('D, YYYY')} @ ${toTime} ET`;
  }

  // Different months
  return `${fromDate.format('MMMM D')} @ ${fromTime} ET - ${toDate.format('MMMM D, YYYY')} @ ${toTime} ET`;
}

/**
 * Get the month name for the ART period
 */
export function getArtPeriodMonth(period: ArtSubmissionInterface): string {
  if (!period.sim_month) {
    return 'Unknown';
  }

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  const monthIndex = parseInt(period.sim_month) - 1;
  return monthNames[monthIndex] || 'Unknown';
}
